import type { ReactNode } from 'react';

/** 素材中台的音色身份及试听快照；id 不属于 IP 音频资产库。 */
export interface CharacterIPTimbre {
  id: string;
  name: string;
  voice_id: number;
  channel: string;
  ext_voice_id: string;
  trial_file: { url: string; };
}

/** MediaAsset / Turnaround 的 UI 适配结构；文档未定义其内部协议，不作为接口 DTO。 */
export interface CharacterIPMedia {
  id: string;
  url: string;
  name: string;
  kind: 'image' | 'video' | 'audio';
  /** 仅明确的生成产物允许前端添加水印。 */
  source?: 'generated' | 'upload';
  thumbnailUrl?: string;
  /** 仅使用媒体事件明确返回的带水印地址，不由原图 URL 推导。 */
  watermarkUrl?: string;
  watermarkCoverUrl?: string;
  /** 正式素材库身份；URL 与预设音色 ID 不作为画布 assetId。 */
  assetId?: string;
  duration?: number;
  fileSize?: number;
  format?: string;
  /** 服务端媒体元数据，用于画布在图片渲染前预留正确的安全区域。 */
  width?: number;
  height?: number;
  createdAt?: number;
  /** 预设音色的列表原文；不是资产身份，保存时必须校验完整音色字段。 */
  voicePreset?: Record<string, unknown>;
  /** 详情回显的音色身份，不能作为素材库 asset_id。 */
  voiceId?: string;
  timbre?: CharacterIPTimbre;
  /** 未接上传服务时仅用于本地预览；宿主持久化前需上传此文件并替换 blob URL。 */
  file?: File;
}

/** 字段名与 PRD 1.0 一致；必填媒体允许 null，以支持未完成的编辑草稿。 */
export interface CharacterIPValue {
  id?: string;
  name: string;
  identity: string;
  summary?: string;
  persona?: string;
  visual_style?: string;
  personality: string[];
  appearance: string;
  speech_style?: string;
  backstory?: string;
  /** 角色头像由 Agent 产出，独立于主视觉，不参与图片替换。 */
  avatar?: CharacterIPMedia | null;
  main_visual: CharacterIPMedia | null;
  turnaround: CharacterIPMedia | null;
  voice?: CharacterIPMedia | null;
  materials?: CharacterIPMedia[];
}

export type CharacterIPImageField = 'main_visual' | 'turnaround';
export type CharacterIPRequiredField = 'name' | 'identity' | 'personality' | 'appearance' | 'summary' | 'persona' | 'speech_style' | 'visual_style' | CharacterIPImageField;
export type CharacterIPPanelAction = 'edit' | 'save_role' | 'basic_settings' | 'more_assets'
| 'main_image_change' | 'main_image_recreate' | 'three_view_change' | 'three_view_recreate'
| 'timbre' | 'ip_name' | 'ip_feature';

// 产品字段表：三视图、性格特点、语言风格、故事、音色和更多素材均为选填。
export const CHARACTER_IP_REQUIRED_FIELDS: readonly CharacterIPRequiredField[] = [
  'name', 'identity', 'appearance', 'main_visual',
];

export type CharacterIPImageUploadErrorCode = 'file_too_large' | 'invalid_file';

export type CharacterIPErrorCode = '15602' | '15603' | '15604' | '15606' | '15608'
| '15616' | '15617' | '15618' | '15619' | '15620';

/** 图片上传适配器使用稳定错误码，让通用面板选择对应的 Smartcat 文案。 */
export class CharacterIPImageUploadError extends Error {
  constructor(public readonly code: CharacterIPImageUploadErrorCode) {
    super(`IP image upload ${code.replaceAll('_', ' ')}`);
    this.name = 'CharacterIPImageUploadError';
  }
}

/** 文案由宿主提供，避免在可复用组件里绕过 Smartcat 写入翻译。 */
export type CharacterIPPanelTexts = Record<
keyof Omit<CharacterIPValue, 'id' | 'summary' | 'persona' | 'visual_style' | 'avatar'> | 'basic' | 'basicImage' | 'characterStory'
| 'edit' | 'saveToLibrary' | 'saveSuccess' | 'applyToCanvas' | 'saveInfo' | 'cancel' | 'create' | 'replace'
| 'regenerate' | 'more' | 'delete' | 'selectAll' | 'empty' | 'required'
| 'invalidImage' | 'actionFailed' | 'play' | 'pause' | 'noVoice', string
> & {
  /** 窄屏标签的短文案；未提供时保留完整名称，不截取翻译字符串。 */
  basicCompact?: string;
  addResource?: string;
  uploadVoice?: string;
  upload?: string;
  /** 上传音色格式不受支持时的提示。 */
  voiceFormatError?: string;
  selectVoice?: string;
  loading?: string;
  summary?: string;
  persona?: string;
  visual_style?: string;
  /** 图片超过业务体积上限时的提示；旧宿主未提供时回退到通用失败提示。 */
  imageTooLarge?: string;
  /** 画布多选超过上限时的提示；未提供时不 toast，仍拦截勾选。 */
  selectionLimit?: string;
  /** IP 接口业务码对应文案；未知业务码回退 actionFailed。 */
  ipErrors?: Partial<Record<CharacterIPErrorCode, string>>;
};

export interface CharacterIPPanelProps {
  value: CharacterIPValue;
  /** 画布弹窗复用只读基础设定与可选素材，操作由画布宿主处理。 */
  canvasActions?: {
    /** 打开面板时按当前画布回显，不覆盖用户后续修改。 */
    initialSelectedMaterialIds?: string[];
    /** 同步模式允许取消全部选择并移除对应画布节点。 */
    allowEmptySelection?: boolean;
    /** 画布详情可保留素材选择与应用，但按宿主要求关闭素材删除入口。 */
    allowMaterialDeletion?: boolean;
    applyLabel: string;
    /** 已自动保存的角色素材允许直接结束操作，不改动画布选区。 */
    saveLabel?: string;
    saveHint?: string;
    closeLabel: string;
    onClose: () => void;
    onApply: (tab: 'basic' | 'materials', materialIds: string[]) => void | Promise<void>;
    onSave?: () => void | Promise<void>;
    onDelete: (materialIds: string[]) => void | Promise<void>;
  };
  texts: CharacterIPPanelTexts;
  /** 全局只读优先于各项编辑能力，保留预览及宿主提供的确认入口。 */
  readOnly?: boolean;
  /** text 默认只编辑文字，可单独启用图片替换；保存信息不等待另一张必填图片生成。 */
  editingMode?: 'all' | 'text';
  /** 单独开放已接入上传的图片替换；autoSave 模式上传后立即持久化。 */
  allowImageReplacement?: boolean;
  /** 仅锁定媒体操作；文字设定仍可编辑，避免未接上传时保存临时资源。 */
  mediaReadOnly?: boolean;
  /** 音色选择和上传可独立于图片/素材锁定状态开放。 */
  voiceReadOnly?: boolean;
  /** 上传成功后返回持久化音频地址；autoSave 模式随即更新主档。 */
  onUploadVoice?: (file: File, signal?: AbortSignal) => Promise<CharacterIPMedia>;
  onLoadVoiceOptions?: (signal: AbortSignal) => Promise<CharacterIPMedia[]>;
  /** 宿主已有顶部确认入口时，可隐藏底部创建和取消操作。 */
  showCreateActions?: boolean;
  /** 创建工作台始终展示编辑控件；文字失焦、图片上传和音色选择后立即持久化。 */
  autoSave?: boolean;
  /** 创建/编辑工作台直接展示可编辑表单，只有显式点击保存信息才提交修改。 */
  manualSave?: boolean;
  /** IP 主档表单展示描述、人设、视觉风格；旧面板默认不增加字段。 */
  showProfileFields?: boolean;
  /** 草稿允许在必填图片尚未生成时逐步保存，文字必填仍在保存信息时校验。 */
  allowIncompleteSave?: boolean;
  requiredFields?: readonly CharacterIPRequiredField[];
  voiceOptions?: CharacterIPMedia[];
  /** 宿主可独立接通真实预设列表；autoSave 模式下选择后立即持久化。 */
  renderVoiceSelector?: (props: CharacterIPVoiceSelectorProps) => ReactNode;
  hasNewMaterials?: boolean;
  onReadMaterials?: () => void;
  onChange?: (value: CharacterIPValue) => void;
  /** 保存信息后的退出保护状态；宿主仅在明确保存信息成功后清除本次修改标记。 */
  onDirtyChange?: (dirty: boolean) => void;
  /** 同时提供编辑起点，宿主可将实际文字和图片改动合入实时数据，并返回最终展示值。 */
  onSave?: (value: CharacterIPValue, initialValue: CharacterIPValue) => void | CharacterIPValue | Promise<void | CharacterIPValue>;
  onSaveToLibrary?: (value: CharacterIPValue) => void | Promise<void>;
  /** 仅在创建工作台提供时展示应用入口；保存主档不会隐式写入画布。 */
  onApplyToCanvas?: (value: CharacterIPValue) => void | Promise<void>;
  onCreate?: (value: CharacterIPValue) => void | Promise<void>;
  onCancel?: () => void;
  /** 取消本地编辑；宿主可在一次保存失败后重新读取权威快照。 */
  onCancelEdit?: () => void;
  onRegenerate?: (field: CharacterIPImageField, media: CharacterIPMedia | null) => void | Promise<void>;
  /** signal 随取消或卸载中止上传；autoSave 模式在上传返回后立即更新主档。 */
  onReplaceImage?: (file: File, field: CharacterIPImageField, signal?: AbortSignal) => Promise<CharacterIPMedia>;
  onPreviewMaterial?: (media: CharacterIPMedia) => void;
  /** 业务宿主可记录面板入口点击，不将项目/画布身份耦合到通用 UI。 */
  onActionClick?: (action: CharacterIPPanelAction) => void;
  /** 音色成功选中或上传后回传真实媒体，由宿主补齐埋点上下文。 */
  onVoiceClick?: (media: CharacterIPMedia, source: 'upload' | 'menu') => void;
  /** 删除由宿主持久化确认；提供此回调可在 text 模式独立开放素材管理。 */
  onDeleteMaterials?: (media: CharacterIPMedia[]) => Promise<void>;
}

export interface CharacterIPVoiceSelectorProps {
  value?: CharacterIPMedia | null;
  disabled: boolean;
  /** 本地音色正在上传时由面板传入，入口需展示 loading 而不是只禁用。 */
  uploading?: boolean;
  onChange: (value: CharacterIPMedia | null) => void;
  onPreviewStart: () => void;
  onOpen?: () => void;
  texts: Pick<CharacterIPPanelTexts, 'voice' | 'noVoice' | 'play' | 'pause' | 'empty' | 'actionFailed'> & Partial<CharacterIPPanelTexts>;
  onUpload?: () => void;
}

/** 保存时忽略全空白性格项，并维持 string[]，不把它误提交成单个字符串。 */
export function normalizeCharacterIP(value: CharacterIPValue): CharacterIPValue {
  return {
    ...value,
    name: value.name.trim(),
    summary: value.summary?.trim(),
    persona: value.persona?.trim(),
    visual_style: value.visual_style?.trim(),
    identity: value.identity.trim(),
    appearance: value.appearance.trim(),
    personality: value.personality.map((item) => item.trim()).filter(Boolean),
    speech_style: value.speech_style?.trim(),
    backstory: value.backstory?.trim(),
  };
}

/** 统一校验信息保存、入库和创建三个出口，包含 PRD 必填的主视觉。 */
export function getMissingCharacterIPFields(
  value: CharacterIPValue,
  requiredFields: readonly CharacterIPRequiredField[] = CHARACTER_IP_REQUIRED_FIELDS,
): CharacterIPRequiredField[] {
  const clean = normalizeCharacterIP(value);
  return requiredFields.filter((key) => {
    if (key === 'personality') return !clean.personality.length;
    if (key === 'main_visual' || key === 'turnaround') return !clean[key]?.url || clean[key]?.kind !== 'image';
    return !clean[key];
  });
}
